import React, { useState, useEffect, useMemo } from 'react';
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, Switch, Button, Typography, Grid, Box, Card, CardContent, CircularProgress, Backdrop, MenuItem, Select, FormControl, InputLabel, IconButton } from '@mui/material';
import { DataGrid, GridToolbar, getGridStringOperators } from '@mui/x-data-grid';

import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend as RechartsLegend, ResponsiveContainer } from 'recharts';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Doughnut } from 'react-chartjs-2';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Chart, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import MapComponent from './MapComponent';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { styled } from '@mui/material/styles';
import { getUserActivityLogs, getOverAllReports } from '../helper/IraHelper';
import * as XLSX from 'xlsx';
import { convertMinutesToCustomFormat,findStateandCity } from '../common/CommonSnippet';


import DashboardPDF from './DashboardPDF';
import dayjs from 'dayjs';

// Register the components
Chart.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title);
Chart.register(ChartDataLabels);

// Configure Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});
const SwitchStyle = styled(Switch)(({ theme }) => ({
    '& .Mui-checked': {
        color: '#7030a0 !important',
    },
    '& .Mui-checked + .MuiSwitch-track': {
        backgroundColor: '#b797cf !important',
    },
}));
const Dashboard = () => {
    const [gridData, setGridData] = useState(null);
    const [pagination, setPagination] = useState({ page: 0, pageSize: 10, total: 0 });
    const [loading, setLoading] = useState(false);
    const [acitivityLoading, setAcitivityLoading] = useState(false);
    const [toolUsageLoading, setToolUsageLoading] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [locationDetails, setLocationDetails] = useState([]);
    const [userType, setUserType] = useState('All');
    const [dateFilter, setDateFilter] = useState('All');
    const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [overallReport, setOverallReport] = useState(null);
    const [pdfOpen, setPdfOpen] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [filterModelUserActivity, setFilterModelUserActivity] = useState({ items: [] });
    const [filterModelToolUsage, setFilterModelToolUsage] = useState({ items: [] });

    useEffect(() => {
        fetchData();
        fetchOverallReport();
        return () => { console.log('is cleared') };
    }, []);

    const fetchData = async (sDate = "", eDate = "") => {
        setAcitivityLoading(true);
        try {
            const payload = {

                "FetchData": (sDate == "" && eDate == "") ? "All" : "Filter",

                "CorpName": "",

                "Name": "",

                "Email": "",

                "BrowserName": "",

                "LocationName": "",

                "StartDate": sDate,

                "EndDate": eDate,

            }

            const response = await getUserActivityLogs(payload);
            const resultsWithId = response?.Results?.map((row, index) => ({
                ...row,
                id: `${row.Id}`,
            }));
            setGridData(resultsWithId);

        } catch (error) {
            console.error('Error fetching data:', error);
        }
        finally {
            setAcitivityLoading(false);
        }
    };


    const fetchOverallReport = async (sDate = "", eDate = "") => {
        setToolUsageLoading(true);
        try {
            const payload = {

                "FetchData": (sDate == "" && eDate == "") ? "All" : "Filter",

                "StartDate": sDate,

                "EndDate": eDate,

            }

            const responseReport = await getOverAllReports(payload);
            if (Array.isArray(responseReport?.OverallReport)) {
                responseReport.OverallReport =
                {
                    "BrowserDetails": [

                    ],
                    "FunctionDetails": [

                    ],
                    "LocationDetails": [

                    ],
                    "MaxUsedFunction": [

                    ],
                    "TotalMinutes": "",
                    "TotalUser": "",
                    "UserDetails": [


                    ]
                }
            }

            if (responseReport?.OverallReport?.UserDetails) {
                responseReport.OverallReport.UserDetails = responseReport.OverallReport?.UserDetails?.map((row, index) => ({
                    ...row,
                    id: `${row.UserId}`,
                }));
                setOverallReport(responseReport);
            } else {
                console.error("UserDetails is undefined");
            }
            setOverallReport(responseReport);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
        finally {
            setToolUsageLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setPagination((prev) => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (newPageSize) => {
        setPagination((prev) => ({ ...prev, pageSize: newPageSize }));
    };

    const handleFilterChange = (newFilterModel) => {
        setFilterModel(newFilterModel);
    };

    const handleRowClick = (params) => {
        setSelectedRecord(params.row);
        setIsDialogOpen(true);
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setSelectedRecord(null);
    };

    const dateInMin = (dateTimeString) => {
        if (!dateTimeString) return null;

        const [datePart, timePart] = dateTimeString.split(' ');
        if (!datePart || !timePart) return null;

        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes, seconds] = timePart.split(':').map(Number);

        const date = new Date(year, month - 1, day, hours, minutes, seconds);
        const milliseconds = date.getTime();
        const minutesSinceEpoch = Math.floor(milliseconds / 60000);

        return minutesSinceEpoch;
    };
    const handleDownloadPDF = () => {
        const input = document.getElementById('dashboard-content');
        html2canvas(input).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF();
            pdf.addImage(imgData, 'PNG', 0, 0);
            pdf.save('dashboard.pdf');
        });
    };
    const toolUsageColumns = [
        {
            field: 'CorpName', headerName: 'Corp Name', width: 100, filterOperators: getGridStringOperators().filter(op =>
                op.value === 'contains' || op.value === 'equals'
            ),
        },
        {
            field: 'Name',
            headerName: 'Name',
            width: 180,
            filterOperators: getGridStringOperators().filter(op =>
                op.value === 'contains' || op.value === 'equals'
            ),
        },
        {
            field: 'UserType',
            headerName: 'User Type',
            width: 150,
            filterOperators: getGridStringOperators().filter(op =>
                op.value === 'contains' || op.value === 'equals'
            ),
        },
        {
            field: 'FrequentFunction', headerName: 'Frequent Function', width: 120, filterOperators: getGridStringOperators().filter(op =>
                op.value === 'contains' || op.value === 'equals'
            )
        },
        {
            field: 'TotalMinutesSpent', headerName: 'Hours Spent', width: 90, filterable: false, renderCell: (params) => { return <div>{convertMinutesToCustomFormat(params?.row?.TotalMinutesSpent)}</div> }
        },
        {
            field: 'FirstSession', headerName: 'First Session', width: 140, filterOperators: getGridStringOperators().filter(op =>
                op.value === 'contains' || op.value === 'equals'
            )
        },
        {
            field: 'LastSession', headerName: 'Last Session', width: 140, filterOperators: getGridStringOperators().filter(op =>
                op.value === 'contains' || op.value === 'equals'
            )
        },
    ]
    const columns = [
        {
            field: 'CorpName', headerName: 'Corp Name', width: 150, filterOperators: getGridStringOperators().filter(op =>
                op.value === 'contains' || op.value === 'equals'
            )
        },

        {
            field: 'Name',
            headerName: 'Name',
            width: 180,
            filterOperators: getGridStringOperators().filter(op =>
                op.value === 'contains' || op.value === 'equals'
            ),
        },
        {
            field: 'UserType',
            headerName: 'User Type',
            width: 150,
            filterOperators: getGridStringOperators().filter(op =>
                op.value === 'contains' || op.value === 'equals'
            ),
        },

        {
            field: 'Email', headerName: 'Email', width: 300, filterOperators: getGridStringOperators().filter(op =>
                op.value === 'contains' || op.value === 'equals'
            )
        },
        {
            field: 'Role', headerName: 'Role', width: 150, filterOperators: getGridStringOperators().filter(op =>
                op.value === 'contains' || op.value === 'equals'
            )
        },
        {
            field: 'BrowserName', headerName: 'Browser Name', width: 150, filterOperators: getGridStringOperators().filter(op =>
                op.value === 'contains' || op.value === 'equals'
            )
        },
        {
            field: 'LocationName', headerName: 'Location Name', width: 150,
            filterOperators: getGridStringOperators().filter(op =>
                op.value === 'contains' || op.value === 'equals'
            )
        },

        { field: 'UserIpAddress', headerName: 'IP Address', width: 150, filterable: false },
        {
            field: 'Duration',
            headerName: 'Time on App (mins)',
            width: 150,
            renderCell: (params) => {
                if (params?.row?.Logout !== null) {

                    const loginTime = dateInMin(params?.row?.LoginTime);
                    const logoutTime = dateInMin(params?.row?.Logout);
                    const timeOnApp = (logoutTime - loginTime);
                    return timeOnApp.toFixed(2);
                } else {
                    return "—";
                }
            },
        },
    ];

    const updatedGridData = gridData?.map((row, index) => ({
        ...row,
        id: row.id || `${row.FirstName}-${row.LastName}-${index}`,
        Name: `${row.FirstName} ${row.LastName}`,
        UserType: `${row.IsTraneEmployee === 1 ? "Trane Technologies" : "Customer"}`,
        LocationName: findStateandCity(row?.LocationName,row?.CountryCode),
    }));

    const applyFilters = (data, filters) => {
        return data.filter((row) => {
            return filters.every(({ field, operator, value }) => {
                if (!value) return true;
                const cellValue = row[field]?.toString().toLowerCase();
                const filterValue = value.toLowerCase();

                if (operator === 'contains') {
                    return cellValue.includes(filterValue);
                } else if (operator === 'equals') {
                    return cellValue === filterValue;
                }
                return true;
            });
        });
    };
    const exportFilteredData = () => {
        const filteredData = applyFilters(updatedGridData, filterModelUserActivity.items);

        if (filteredData.length === 0) {
            alert("No data to export. Apply a filter first!");
            return;
        }
        const exportData = filteredData.map(row => ({
            'Corp Name': row.CorpName,
            'Name': `${row.FirstName} ${row.LastName}`,
            'User Type': `${row.IsTraneEmployee === 1 ? "Trane Technologies" : "Customer"}`,
            'Email': row.Email,
            'Role': row.Role,
            'Browser Name': row.BrowserName,
            'City': row.LocationName?.split(',')[0],
            'State': row.LocationName?.split(',')[1] || "",
            'IP Address': row.UserIpAddress,
            'Time on App (mins)': row.Duration || "—",
        }));
        console.log("export user activity: ", exportData)
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "FilteredData");
        XLSX.writeFile(wb, "IRA_User_Activity_Data.xlsx");
    };
    const pieData = selectedRecord?.FunctionUsageDetails.map(item => ({
        name: `${item.FunctionName}`,
        value: item.UsageCount,
    })) || [];
    const handleDateFilter = () => {
        if (startDate && endDate) {
            fetchData(startDate, endDate);
            fetchOverallReport(startDate, endDate);
        }
    }
    const updateDatefilter = (val) => {
        if (val == "All") {
            fetchData();
            fetchOverallReport();
        }
        setDateFilter(val);
    }
    const COLORS = [
        '#0088FE',
        '#00C49F',
        '#FFBB28',
        '#FF8042',
        '#A28BFE',
        '#FF6384',
        '#36A2EB',
        '#FFCE56',
        '#4BC0C0',
        '#9966FF'
    ];

    if (!overallReport) {
        return (
            <Backdrop open={true}>
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    const { BrowserDetails, LocationDetails, FunctionDetails, TotalUser, UserDetails, TotalMinutes, MaxUsedFunction } = overallReport?.OverallReport;


    const updatedToolDataGrid = UserDetails?.map((row, index) => ({
        ...row,
        id: row.id || `${row.FirstName}-${row.LastName}-${index}`,
        Name: `${row.FirstName} ${row.LastName}`,
        UserType: `${row.IsTraneEmployee === 1 ? "Trane Technologies" : "Customer"}`,
    }));
    const browserPieData = BrowserDetails.map(item => ({
        name: item.BrowserName,
        value: item.Count,
    }));

    const functionBarData = FunctionDetails.map(item => ({
        name: item.FunctionName,
        count: item.Count,
    }));

    const locationMarkers = LocationDetails.map((location, index) => (
        <Marker key={index} position={[location.latitude, location.longitude]}>
            <Popup>
                {location.CountryCode}: {location.Count}
            </Popup>
        </Marker>
    ));

    const centerTextPlugin = {
        id: 'centerText',
        beforeDraw: function (chart) {
          const ctx = chart.ctx;
          const width = chart.width;
          const height = chart.height;
          const fontSize = (height / 114).toFixed(2);
          ctx.restore();
          ctx.font = `${fontSize}em sans-serif`;
          ctx.textBaseline = 'middle';
      
          const text = chart.config.data.datasets[0].data[0].toString();
          const textX = Math.round((width - ctx.measureText(text).width) / 2);
          const textY = height / 2;
          ctx.fillStyle = chart.config.data.datasets[0].backgroundColor[0];
          ctx.fillText(text, textX, textY);
          ctx.save();
        },
      };
    return (
        <div style={{ width: "100%" }}>

            <div>
                <h2 className='dashHead'>Tool Activity Analytics<span style={{
                    float: "right",
                    height: "inherit",
                    lineHeight: "2"
                }}> Show Filter:

                    <SwitchStyle
                        className='styledSwitch'
                        checked={showFilter}
                        onChange={() => setShowFilter(!showFilter)}
                        inputProps={{ 'aria-label': 'controlled' }}
                    />

                </span></h2>

                {showFilter && (<Box className="filterBox" sx={{ padding: 1, display: 'flex', justifyContent: 'right' }}>
                    <Box className="filterData" sx={{ display: 'flex', gap: 2, alignItems: 'right', marginBottom: 1 }}>
                        <Grid item xs={12} md={6}>
                            <FormControl sx={{ minWidth: 100 }} className='filterlabel'>
                                <InputLabel className='dateFilter'>Date Filter</InputLabel>
                                <Select
                                    value={dateFilter}
                                    onChange={(e) => updateDatefilter(e.target.value)}
                                >
                                    <MenuItem value="All">All</MenuItem>
                                    <MenuItem value="Specific">Specific</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {dateFilter === 'Specific' && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        className='filterlabel'
                                        label="Start Date"
                                        type="date"
                                        fullWidth
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        className='filterlabel'
                                        label="End Date"
                                        type="date"
                                        fullWidth
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                            </>
                        )}

                        <Button variant="contained" style={dateFilter === 'Specific' ? { background: "#7030a0", color: '#fff' } : { background: "#7030a0", color: '#fff', opacity: '0.5' }} onClick={handleDateFilter} disabled={dateFilter === 'Specific' ? false : true} >Update Date</Button>
                        <Button variant="contained" style={{ background: "#7030a0" }} onClick={() => setPdfOpen(true)}>Export</Button>

                    </Box>
                    <DashboardPDF
                        open={pdfOpen}
                        onClose={() => setPdfOpen(false)}
                        overallReport={overallReport} />
                </Box>)}
                <div id="dashboard-content">
                    <Grid container spacing={3} paddingLeft="2rem">
                        <div className='initialRowDashboard'>
                            <div className='initialRow1Col1' style={{ display: 'flex', flexDirection: 'column', gap: "1rem" }} >
                                <div>
                                    <Grid item xs={12} md={12}>
                                        <Card style={{ background: '#fafafa' }}>
                                            <CardContent style={{ paddingBottom: '1rem' }}>
                                                <Typography style={{ textAlign: 'left', fontWeight: 'bold' }} variant="h5">Max Used Functions</Typography>
                                                <div style={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'center', gap: '1rem', padding: "7px" }}>
                                                    {MaxUsedFunction.length > 0 ? (
                                                        MaxUsedFunction.map((max, index) => (
                                                            <Typography key={`max-${index}`} variant="h6" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
                                                                <span style={{ height: '0.6rem', width: '1.5rem', color: COLORS[index % COLORS.length], background: COLORS[index % COLORS.length], borderRadius: '2px', lineHeight: '0' }}>- </span>
                                                                {`${max.FunctionName} - ${max.Count}`}
                                                            </Typography>
                                                        ))
                                                    ) : (
                                                        <Typography variant="h6" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
                                                            <span style={{ height: '0.6rem', width: '1.5rem', color: "#d3d3d3", background: "#d3d3d3", borderRadius: '2px', lineHeight: '0' }}>- </span>
                                                            {"No Function Used"}
                                                        </Typography>
                                                    )}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </div>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <Grid item xs={12} md={6}>
                                        <Card style={{ background: '#fafafa' }}>
                                            <CardContent style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                                                <Typography style={{ width: '100%', textAlign: 'left', fontWeight: 'bold' }} variant="h5">Total Users</Typography>
                                                <div style={{ height: '200px', width: "90%" }}>
                                                    <Doughnut
                                                        data={{
                                                            labels: ['Total Users'],
                                                            datasets: [{
                                                                data: [TotalUser ? TotalUser : 0],
                                                                backgroundColor: ['#FF6384'],
                                                            }]
                                                        }}
                                                        options={{
                                                            maintainAspectRatio: false,
                                                            responsive: true,
                                                            plugins: {
                                                                legend: {
                                                                    display: false,
                                                                },
                                                                datalabels: {
                                                                    display: false,
                                                                },
                                                            },
                                                        }}
                                                        plugins={[centerTextPlugin]}
                                                    />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Card style={{ background: '#fafafa' }}>
                                            <CardContent style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                                                <Typography style={{ width: '100%', textAlign: 'left', fontWeight: 'bold' }} variant="h5">Total Hours</Typography>
                                                <div style={{ height: '200px', width: "90%" }}>
                                                    <Doughnut
                                                        data={{
                                                            labels: ['Total Hours'],
                                                            datasets: [{
                                                                data: [TotalMinutes ? convertMinutesToCustomFormat(TotalMinutes) : 0],
                                                                backgroundColor: ['#36A2EB'],
                                                            }]
                                                        }}
                                                        options={{
                                                            maintainAspectRatio: false,
                                                            responsive: true,
                                                            plugins: {
                                                                legend: {
                                                                    display: false,
                                                                },
                                                                datalabels: {
                                                                    display: false,
                                                                },
                                                            },
                                                        }}
                                                        plugins={[centerTextPlugin]}
                                                    />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </div>
                            </div>

                            <div className='initialRow1Col2' >
                                <Grid item xs={12} md={12}>
                                    <Card style={{ background: '#fafafa' }}>
                                        <CardContent>
                                            <Typography style={{ textAlign: 'left', fontWeight: 'bold' }} variant="h5">
                                                Browser Details
                                            </Typography>
                                            <div style={{ display: 'flex', alignItems: 'center', height: 265 }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart width={269} height={265}>
                                                        <Pie
                                                            data={browserPieData.length > 0 ? browserPieData : [{ name: 'No Data', value: 1 }]}
                                                            cx="50%"
                                                            cy="50%"
                                                            labelLine={false}
                                                            label={({ name, percent }) => browserPieData.length > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : `${name}`}
                                                            outerRadius={90}
                                                            fill="#8884d8"
                                                            dataKey="value"
                                                        >
                                                            { }
                                                            {browserPieData.length > 0
                                                                ? browserPieData.map((entry, index) => (
                                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                                ))
                                                                : <Cell fill="#d3d3d3" />}
                                                        </Pie>
                                                        <RechartsTooltip />
                                                        <RechartsLegend layout="vertical" verticalAlign="bottom" align="right" />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </div>

                            <div className='initialRow1Col3'>
                                <Grid className='gridArea' item xs={12} md={12}>
                                    <Card style={{ background: '#fafafa' }}>
                                        <CardContent style={{ maxHeight: '282px' }}>
                                            <Typography style={{ textAlign: 'left', fontWeight: 'bold' }} variant="h5">Function Details</Typography>
                                            <div style={{ width: '100%', height: '280px' }}>
                                                <ResponsiveContainer>
                                                    <BarChart data={functionBarData.length > 0 ? functionBarData : [{ name: 'No Data', value: 1 }]}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="name" />
                                                        <YAxis />
                                                        <RechartsTooltip />
                                                        <RechartsLegend />
                                                        <Bar dataKey="count" fill="#8884d8" />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </div>

                        </div>


                        <Grid item xs={12} md={6} style={{ padding: "11px", paddingRight: 0 }}>
                            <Card style={{ background: '#fafafa' }}>
                                <CardContent>
                                    <Typography style={{ textAlign: 'left', fontWeight: 'bold' }} variant="h5">Location Details</Typography>
                                    <MapComponent locationDetails={LocationDetails} />
                                    <p style={{textAlign:'left', margin:'0'}}>*The location of certain users is unavailable;</p>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6} style={{ padding: "11px" }}>
                            <Card style={{ background: '#fafafa' }}>
                                <CardContent >
                                    <Typography style={{ textAlign: 'left', fontWeight: 'bold' }} variant="h5">Tool Usage Details</Typography>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            '& .super-app-theme--header': {
                                                backgroundColor: 'rgba(255, 7, 0, 0.55)',
                                            },
                                        }}
                                    >
                                        <DataGrid
                                            rows={updatedToolDataGrid}
                                            columns={toolUsageColumns}
                                            pageSize={5}
                                            rowsPerPageOptions={[5]}
                                            className='dashGrid dashGridTime'
                                            style={{ height: '314px', maxWidth: '700px' }}
                                            hideFooter
                                            autoWidth={false}
                                            disableExtendRowFullWidth
                                            loading={toolUsageLoading}
                                            components={{ Toolbar: GridToolbar }}
                                            filterModel={filterModelToolUsage}
                                            onFilterModelChange={setFilterModelToolUsage}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </div>
            </div>
            <h2 className='dashHead'>User Activity Monitoring<span style={{
                float: "right",
                height: "inherit",
                lineHeight: "2",
                paddingRight: '12px'
            }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={exportFilteredData}
                    style={{ background: "#7030a0" }}
                >
                    Export
                </Button></span></h2>
            <div style={{ padding: '1rem' }}>
                <Box
                    sx={{
                        height: 500,
                        width: '100%',
                        '& .super-app-theme--header': {
                            backgroundColor: 'rgba(255, 7, 0, 0.55)',
                        },
                    }}
                >
                    <DataGrid
                        rows={updatedGridData}
                        columns={columns}
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                        onRowClick={handleRowClick}
                        className='dashGrid dashGridTime'
                        loading={acitivityLoading}
                        components={{ Toolbar: GridToolbar }}
                        filterModel={filterModelUserActivity}
                        onFilterModelChange={setFilterModelUserActivity}
                        sx={{
                            '& .MuiTablePagination-actions .MuiTablePagination-root': { color: 'black' },
                            '& .MuiTablePagination-actions .MuiSvgIcon-root': { color: 'black !important' },
                        }}
                    />
                </Box>
            </div>
            <Dialog open={isDialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
                <DialogTitle></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Grid container spacing={10}>
                            <Grid item xs={12} sm={5}>
                                <Typography variant="h3" style={{ fontWeight: 'bold', textAlign: 'center' }}>User Details</Typography>
                                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '1rem', height: '300px', paddingLeft: "40px" }}>
                                    <Typography style={{ display: "flex", gap: "2rem", justifyContent: 'space-between' }}><span><strong>Corp Name:</strong></span> <span> {selectedRecord.CorpName} </span></Typography>
                                    <Typography style={{ display: "flex", gap: "2rem", justifyContent: 'space-between' }}><span><strong>Name:</strong></span> <span> {selectedRecord.FirstName} {selectedRecord.LastName} </span></Typography>
                                    <Typography style={{ display: "flex", gap: "2rem", justifyContent: 'space-between' }}><span><strong>User Type:</strong></span> <span>{selectedRecord.IsTraneEmployee === 1 ? "Trane Technologies" : "Customer"} </span></Typography>
                                    <Typography style={{ display: "flex", gap: "2rem", justifyContent: 'space-between' }}><span><strong>Email:</strong></span> <span> {selectedRecord.Email} </span></Typography>
                                    <Typography style={{ display: "flex", gap: "2rem", justifyContent: 'space-between' }}><span><strong>Role:</strong></span> <span> {selectedRecord.Role} </span></Typography>
                                    <Typography style={{ display: "flex", gap: "2rem", justifyContent: 'space-between' }}><span><strong>Browser Name:</strong></span> <span> {selectedRecord.BrowserName} </span></Typography>
                                    <Typography style={{ display: "flex", gap: "2rem", justifyContent: 'space-between' }}><span><strong>Location Name:</strong></span> <span> {selectedRecord.LocationName} </span></Typography>
                                    <Typography style={{ display: "flex", gap: "2rem", justifyContent: 'space-between' }}><span><strong>IP Address:</strong></span> <span> {selectedRecord.UserIpAddress} </span></Typography>
                                    <Typography style={{ display: "flex", gap: "2rem", justifyContent: 'space-between' }}><span><strong>Login Time:</strong></span> <span> {selectedRecord.LoginTime} </span></Typography>
                                    <Typography style={{ display: "flex", gap: "2rem", justifyContent: 'space-between' }}><span><strong>Logout Time:</strong></span> <span> {selectedRecord.Logout ? selectedRecord.Logout : "—"} </span></Typography>
                                    <Typography style={{ display: "flex", gap: "2rem", justifyContent: 'space-between' }}><span><strong>Duration (mins):</strong></span> <span> {selectedRecord.Logout ? ((dateInMin(selectedRecord.Logout) - dateInMin(selectedRecord.LoginTime))).toFixed(2) : "—"} </span></Typography>
                                </div>
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                <Typography variant="h3" style={{ fontWeight: 'bold', textAlign: 'center' }}>Activity Log</Typography>
                                <PieChart width={430} height={300} style={{ paddingLeft: "30px" }}>
                                    <Pie
                                        data={pieData.length > 0 ? pieData : [{ name: 'No Data', value: 1 }]}
                                        cx="54%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => pieData.length > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : `${name}`}
                                        outerRadius={90}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        { }
                                        {pieData.length > 0
                                            ? pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))
                                            : <Cell fill="#d3d3d3" />}
                                    </Pie>
                                    <RechartsTooltip />
                                    <RechartsLegend align="right" verticalAlign="bottom" />
                                </PieChart>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Dashboard;
